import { useMemo } from "react";
import useSWR from "swr";
import { find, keyBy } from "lodash";
import { fetchAiccSttModels } from "services/aiccLearningAssets";
import { AICC_SWR_CONFIG } from "./swrConfig";

/**
 * The STT engine catalogue, from `GET /api/admin/stt/models`.
 *
 * 🔴 IT REPLACES A HARDCODED LIST, and the drift it had accumulated is the argument for reading it: the
 * four engines in `SttSetting/configs.tsx` included one the backend had RETIRED — a room silently degrades
 * that one to its successor, so the screen named an engine no conversation used — and omitted the two
 * that replace it, one of which is what new languages are now created with. A stored id that is not in
 * the list prints raw, so the drift showed up as a bare `openai_gpt_transcribe_batch` on the screen.
 *
 * Shaped like `useAiccPromptModels` on purpose: same SWR config, same `byKey` map, same "the backend names
 * its own defaults" rule. `defaultPrimary` / `defaultFallback` are what a language that configures nothing
 * actually runs, which no list in this repo could have said.
 */
export function useAiccSttModels() {
  const { data, error, isLoading, mutate } = useSWR(["aicc/stt/models"], () => fetchAiccSttModels(), AICC_SWR_CONFIG);

  // Memoised for the reason the prompt hook memoises: this array feeds a column config, and a fresh one
  // every render would rebuild the columns every render.
  const options = useMemo(() => data ?? [], [data]);

  return {
    options,
    optionsByKey: useMemo(() => keyBy(options, "key"), [options]),
    defaultPrimary: useMemo(() => find(options, "isDefaultPrimary"), [options]),
    defaultFallback: useMemo(() => find(options, "isDefaultFallback"), [options]),
    isLoading,
    isError: !!error,
    mutate,
  };
}
